import { memo, useMemo } from "react";
import { DIRS, type Dir, fmtHHMM, fmtRiders, stationLabel } from "../demand/demandMath";
import type { Run } from "./Scorecard";
import { type StopDirResult, stopDirIndex } from "./types.ts";

interface Props {
  runs: Run[];
  dirNames: Record<Dir, string>;
}

const SPARK_W = 150;
const SPARK_H = 26;

/** The queue over the day as a small area; a second run is drawn over it as a line. */
function Spark({ series, dir, span, max }: { series: StopDirResult[]; dir: Dir; span: [number, number]; max: number }) {
  const [from, to] = span;
  const x = (min: number) => ((min - from) / Math.max(1, to - from)) * SPARK_W;
  const y = (v: number) => SPARK_H - (Math.min(v, max) / Math.max(1, max)) * (SPARK_H - 2);
  const path = (sd: StopDirResult, close: boolean) => {
    const step = Math.max(1, Math.floor((to - from) / SPARK_W));
    let d = `M${x(from)},${y(sd.waiting[from] ?? 0)}`;
    for (let m = from + step; m <= to; m += step) d += ` L${x(m).toFixed(1)},${y(sd.waiting[Math.min(m, sd.waiting.length - 1)] ?? 0).toFixed(1)}`;
    if (close) d += ` L${x(to)},${SPARK_H} L${x(from)},${SPARK_H} Z`;
    return d;
  };
  return (
    <svg className="sim-spark" width={SPARK_W} height={SPARK_H} aria-hidden>
      <line className="dm-axis" x1={0} x2={SPARK_W} y1={SPARK_H - 0.5} y2={SPARK_H - 0.5} />
      {series[0] && <path className={`sim-spark-area sim-spark-${dir}`} d={path(series[0], true)} />}
      {series[1] && <path className={`sim-spark-line sim-spark-${dir}`} d={path(series[1], false)} />}
    </svg>
  );
}

const minutes = (v: number) => (v < 10 ? v.toFixed(1) : String(Math.round(v)));

/**
 * What riders at each stop went through: the queue over the day, their waits,
 * and who was left standing. With two timetables the draft's figure leads and
 * the active timetable's follows in muted ink, so the eye can run down one
 * column and still see what changed.
 */
function WaitingTable({ runs, dirNames }: Props) {
  const stops = runs[0]?.result.input.stops ?? [];
  const compare = runs.length === 2;
  const lead = runs[runs.length - 1];
  const prior = compare ? runs[0] : null;

  const { span, max } = useMemo(() => {
    let from = Infinity;
    let to = -Infinity;
    let max = 0;
    for (const run of runs) {
      from = Math.min(from, run.result.totals.firstMin);
      to = Math.max(to, run.result.totals.lastMin);
      for (const sd of run.result.stopDirs) max = Math.max(max, sd.maxWaiting);
    }
    return { span: [Number.isFinite(from) ? from : 0, Number.isFinite(to) ? to : 1440] as [number, number], max };
  }, [runs]);

  const rows = useMemo(() => {
    const out: Array<{ stop: number; dir: Dir; lead: StopDirResult; prior: StopDirResult | null }> = [];
    for (const s of stops) {
      for (const dir of DIRS) {
        const i = stopDirIndex(s.index, dir);
        const a = lead.result.stopDirs[i];
        const b = prior?.result.stopDirs[i] ?? null;
        if (!a || (a.arrivals < 0.5 && (b?.arrivals ?? 0) < 0.5)) continue;
        out.push({ stop: s.index, dir, lead: a, prior: b });
      }
    }
    return out;
  }, [stops, lead, prior]);

  const cell = (value: number, was: number | null, format: (v: number) => string, flag = false) => (
    <td className="num">
      <span className={flag && value >= 0.5 ? "dm-flag" : undefined}>{format(value)}</span>
      {was != null && <span className="sim-was">was {format(was)}</span>}
    </td>
  );

  if (rows.length === 0) return null;

  return (
    <div className="dm-chart sim-wait">
      <div className="dm-chart-head">
        <div>
          <h4>Where riders wait</h4>
          <p className="dm-chart-sub">
            Riders waiting at each stop through the day{compare ? ` — the ${runs[0].label.toLowerCase()} as the shaded area, the ${runs[1].label.toLowerCase()} as the line` : ""}, and what the wait came to.
            {compare && ` Figures are the ${runs[1].label.toLowerCase()}'s; "was" is the ${runs[0].label.toLowerCase()}'s.`}
          </p>
        </div>
      </div>
      <div className="dm-table-scroll">
        <table className="data-table compact dm-table sim-wait-table">
          <thead>
            <tr>
              <th>Stop</th>
              <th>Direction</th>
              <th>Waiting through the day</th>
              <th className="num">Most waiting</th>
              <th className="num">Average wait</th>
              <th className="num">Longest wait</th>
              <th className="num">Left behind</th>
              <th className="num">No bus came</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.stop}-${r.dir}`}>
                <th scope="row">{stationLabel(stops[r.stop].name)}</th>
                <td><span className={`dm-swatch dm-swatch-${r.dir}`} aria-hidden /> {dirNames[r.dir]}</td>
                <td className="sim-spark-cell">
                  <Spark series={r.prior ? [r.prior, r.lead] : [r.lead]} dir={r.dir} span={span} max={max} />
                </td>
                <td className="num">
                  {fmtRiders(r.lead.maxWaiting)}
                  {r.lead.maxWaiting >= 1 && <span className="sim-was">at {fmtHHMM(r.lead.maxWaitingAt)}</span>}
                </td>
                {cell(r.lead.waitMeanMin, r.prior?.waitMeanMin ?? null, (v) => `${minutes(v)} min`)}
                {cell(r.lead.waitMaxMin, r.prior?.waitMaxMin ?? null, (v) => `${minutes(v)} min`)}
                {cell(r.lead.leftBehind, r.prior?.leftBehind ?? null, fmtRiders, true)}
                {cell(r.lead.unserved, r.prior?.unserved ?? null, fmtRiders, true)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dm-legend">
        <span className="dm-legend-item">Queues drawn to one scale: the tallest is {fmtRiders(max)} riders.</span>
        <span className="dm-legend-item"><span className="dm-flag">Red</span> figures are riders who did not get on the bus they came for.</span>
      </div>
    </div>
  );
}

export default memo(WaitingTable);
